import { Directive, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SubscriptionService } from "../../subscription/subscription.service";
import { BuyService } from "../../buy/buy.service";
import { Model } from "../../app.model";
import { HttpClient } from "@angular/common/http";
import { CompanyService } from "../../company/company.service";
import { WorkflowPartnerService } from "./workflow-partner.service";
import { firstValueFrom } from "rxjs";
import { Subscription } from "../../subscription/subscription.model";
import { SaleOrder, PaymentMethodCode, PaymentDetails } from "../../buy/buy.model";
import { observable } from "@myshared/mobx-connector";
import { runInAction } from 'mobx';
import { Company, Contact } from "../../company/company.model";
import { WorkflowComponent } from "../workflow.component";
import { Customer } from "src/app/customer/customer.model";
import { CustomerService } from "src/app/customer/customer.service";

export type BaseWorkflowModel = {
    subscription?: Subscription;
    saleOrder?: SaleOrder;
    company?: Company;
    customer?: Customer;
    contact?: Contact;
}

@Directive()
export abstract class WorkflowPartnerComponent<T extends BaseWorkflowModel> extends WorkflowComponent implements OnInit, OnDestroy {

    @observable
    public model: T;

    private contactParam: string;
    private saleOrderParam: string;

    protected constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected subscriptionService: SubscriptionService,
        protected buyService: BuyService,
        protected m: Model,
        protected http: HttpClient,
        protected workflowPartnerService: WorkflowPartnerService,
        protected companyService: CompanyService,
        protected customerService: CustomerService
    ) {
        super();
    }

    public async ngOnInit() {
        super.ngOnInit();
        runInAction(() => {
            this.model = {
                ...this.model
            }
        });

        if (this.applianceId) {
            await this.loadSubscription()
        }

        this.saleOrderParam = this.route.snapshot.queryParamMap.get('so');
        this.contactParam = this.route.snapshot.queryParamMap.get('con');

        if (!this.model.saleOrder && this.saleOrderParam ) {
            await this.loadSaleOrder(this.saleOrderParam );
        }

        if (!this.model.contact && this.contactParam) {
            await this.loadPartnerContext();
        }
    }

    /**
     * Always set a workflow name because navigate function need this
     */
    abstract get workflowName(): string;

    /**
     * Only if a query param is needed
     * like sale order: ?so=SO12345
     */
    protected get queryParams(): { [e:string]: any } | undefined {
        return undefined;
    }

    protected navigate(url: (string | number)[]) {
        this.router.navigate(['/do', 'partner', this.workflowName, this.applianceId, ...url], {
            queryParams: {
                ...this.queryParams,
                so: this.model.saleOrder?.name ?? undefined,
                con: this.model.contact?.id ?? undefined,
            }
        });
    }

    protected async loadSubscription(): Promise<void> {
        const subscription = await firstValueFrom(this.subscriptionService.getSubscriptionDetails(+this.applianceId));
        runInAction(() => {
            this.model.subscription = Subscription.fromJson(subscription);
        })
    }

    protected async loadPartnerContext() {
        const customerId = this.model.subscription.customer_id;

        const [company, customers] = await Promise.all([
            await firstValueFrom(
                this.companyService.getCompany(customerId)
            ),
            await firstValueFrom(this.customerService.getCustomers())
        ])

        const customer = customers.find(c => c.id === customerId);

        let contact: Contact;
        if (!!this.contactParam) {
            contact = customer.contacts.find(r => +r.id === +this.contactParam);
        } else {
            contact = customer.contacts.find(r => !!r.email && !!r.first_name && !!r.last_name);
        }

        runInAction(() => {
            this.model.company = company;
            this.model.customer = customer;
            this.model.contact = contact;
        })
    }

    protected get checkSubscriptionValid(): boolean {
        if (!this.model.subscription) {
            return false;
        }

        if (this.model.subscription.isCancelled) {
            return false;
        }

        if (!this.m.account.currentUser.companyIsPartner) {
            return false;
        }

        if (this.model.subscription.is_my) {
            return false;
        }

        return true;
    }

    protected async setPaymentDetails({ method, bank }: PaymentDetails) {
        if (bank && bank.acc_number && method === 'sepa') {
            await firstValueFrom(this.companyService.updateBankAccount(bank));
            runInAction(() => {
                this.model.company.bankAccounts[0] = bank;
            })
        }

        Promise.resolve();
    }

    protected async loadSaleOrder(so: string): Promise<SaleOrder> {
        return await firstValueFrom(this.buyService.getSaleOrder(so));
    }

    protected get isSepa(): boolean {
        return this.paymentMethod === 'sepa';
    }

    public get paymentMethod(): PaymentMethodCode {
        return this.model?.saleOrder?.payment_method ?? this.model?.subscription?.payment_method;
    }

    protected get hasBankAccount() {
        return this.model.company.hasBankAccount;
    }

    protected get isPaymentStepNeeded() {
        return this.isSepa && !this.hasBankAccount;
    }

    public onComplete() {
        this.resetWorkflow();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
